import { Observable } from 'rxjs';
import { share, tap } from 'rxjs/operators';

import { HttpApiService } from '@recruitee/http';

import { AppStore } from './app-store';
import { Bucket, BucketsStore } from './buckets-store';
import { RepoEventNamespaces } from './types';

export abstract class BaseRepo {
  constructor(
    protected store: BucketsStore,
    protected httpApi: HttpApiService,
    protected appStore: AppStore,
  ) {}

  protected attachRequestToBucket<T = any>(
    bucket: Bucket,
    options: { eventsNamespace?: string } = {},
  ): (request: Observable<T>) => Observable<T> {
    const { eventsNamespace = 'get' } = options;
    return request =>
      request.pipe(
        this.updateBucketStateWithRequest(bucket),
        this.publishRequestEventsOnBucket(bucket, { eventsNamespace }),
      );
  }

  protected updateBucketStateWithRequest<T = any>(
    bucket: Bucket,
  ): (request: Observable<T>) => Observable<T> {
    return request => {
      bucket.setMeta({ ...bucket.meta, loading: true, error: null });
      return request.pipe(
        tap(
          data => {
            bucket.setData(data);
            bucket.setMeta({
              ...bucket.meta,
              loaded: true,
              loading: false,
              failed: false,
            });
          },
          error => {
            let meta;
            if (error && error.error && error.error.meta) {
              meta = error.error.meta;
            }
            bucket.setMeta({
              ...bucket.meta,
              failed: true,
              loading: false,
              error: error ? { status: error.status } : undefined,
              meta,
            });
          },
        ),
      );
    };
  }

  protected publishRequestEventsOnBucket<T = any>(
    bucket: Bucket,
    options: { eventsNamespace?: string } = {},
  ): (request: Observable<T>) => Observable<T> {
    const { eventsNamespace = 'get' } = options;
    return request => {
      bucket.pushEvent({ type: `${eventsNamespace}.loading` });
      return request.pipe(
        tap(
          data => {
            bucket.pushEvent({ type: `${eventsNamespace}.success`, data });
          },
          error => {
            bucket.pushEvent({ type: `${eventsNamespace}.fail`, error });
          },
          () => {
            bucket.pushEvent({ type: `${eventsNamespace}.complete` });
          },
        ),
      );
    };
  }

  // eslint-disable-next-line max-lines-per-function
  protected publishRequestEventsOnAppStore<T = any>(
    eventsNamespace: RepoEventNamespaces,
    options: { bucket?: Bucket; additionalData?: any } = {},
  ): (request: Observable<T>) => Observable<T> {
    const { bucket, additionalData } = options;
    return request => {
      this.appStore.dispatch({ type: `${eventsNamespace}.loading`, bucket, additionalData });

      const requestObservable = request.pipe(
        tap(
          data => {
            this.appStore.dispatch({
              type: `${eventsNamespace}.success`,
              data,
              bucket,
              additionalData,
            });
          },
          error => {
            this.appStore.dispatch({
              type: `${eventsNamespace}.fail`,
              error,
              bucket,
              additionalData,
            });
          },
          () => {
            this.appStore.dispatch({ type: `${eventsNamespace}.complete`, bucket, additionalData });
          },
        ),
        share(),
      );

      // Subscribe empty function because angular clears requests without subscriptions on route change
      requestObservable.subscribe(
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        () => {},
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        () => {},
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        () => {},
      );

      return requestObservable;
    };
  }

  protected publishAsyncJobEventsOnAppStore<T = any>(
    eventsNamespace: RepoEventNamespaces,
    options: { bucket?: Bucket } = {},
  ): (request: Observable<T>) => Observable<T> {
    const { bucket } = options;
    return request => {
      this.appStore.dispatch({ type: `${eventsNamespace}.loading`, bucket });

      const requestObservable = request.pipe(
        tap(
          data => {
            this.appStore.dispatch({ type: `${eventsNamespace}.pending`, data, bucket });
          },
          error => {
            this.appStore.dispatch({ type: `${eventsNamespace}.fail`, error, bucket });
          },
          () => {
            // TODO return data as well in success event
            this.appStore.dispatch({ type: `${eventsNamespace}.success`, bucket });
            this.appStore.dispatch({ type: `${eventsNamespace}.complete`, bucket });
          },
        ),
        share(),
      );

      // Subscribe empty function because angular clears requests without subscriptions on route change
      requestObservable.subscribe(
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        () => {},
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        () => {},
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        () => {},
      );

      return requestObservable;
    };
  }
}
